import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, KeyRound, PlugZap, ShieldCheck, Telescope, XCircle } from 'lucide-react'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { formatDateTime, intlLocale } from '../../lib/datetime'
import { toast } from '../../lib/toast'
import { ConfiguredBadge, LockedCredentialNotice } from '../../components/credential-lock'
import { useEditGate } from '../../components/credential-lock.lib'
import { useT } from '../../i18n'
import { researchToolsI18n } from '../../i18n/admin/research-tools'

type ResearchToolCredentialRead = {
  id: string
  provider: 'alura'
  api_key_hint: string | null
  has_api_key: boolean
  last_validated_at: string | null
  created_at: string
  updated_at: string
}

type ResearchToolCredentials = {
  items: ResearchToolCredentialRead[]
  total: number
}

type ConnectionTest = {
  ok: boolean
  workspace_name: string | null
  plan: string | null
  credits_remaining: number | null
  error_code: string | null
  message: string | null
}

const QUERY_KEY = ['admin-research-tool-credentials'] as const
const BASE = '/admin/research-tool-credentials'
const INPUT_CLASS =
  'w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] font-mono text-[var(--color-text)] outline-none focus:border-indigo-300 focus:shadow-[0_0_0_3px_rgb(238_242_255)]'

async function fetchCredentials(): Promise<ResearchToolCredentials> {
  const response = await fetch(apiUrl(BASE))
  if (!response.ok) throw new Error(`fetch research tool credentials ${response.status}`)
  return (await response.json()) as ResearchToolCredentials
}

function errorMessage(body: unknown, fallback: string): string {
  if (
    body &&
    typeof body === 'object' &&
    'detail' in body &&
    body.detail &&
    typeof body.detail === 'object' &&
    'message' in body.detail &&
    typeof body.detail.message === 'string'
  ) {
    return body.detail.message
  }
  return fallback
}

async function request<T>(path: string, init: RequestInit): Promise<T | null> {
  const response = await fetch(apiUrl(path), init)
  if (!response.ok) {
    throw new Error(errorMessage(await response.json().catch(() => null), `request failed (${response.status})`))
  }
  return response.status === 204 ? null : ((await response.json()) as T)
}

export function ResearchTools() {
  const t = useT(researchToolsI18n)
  const queryClient = useQueryClient()
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: fetchCredentials })
  const current = query.data?.items.find((item) => item.provider === 'alura') ?? null

  return (
    <div className="space-y-4">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-[var(--color-text)]">
          <Telescope size={22} className="text-indigo-600" aria-hidden="true" />
          {t.title}
        </h1>
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
          <ShieldCheck size={12} className="text-[var(--color-success)]" aria-hidden="true" />
          {t.encryptedNote}
        </p>
      </header>

      {!USE_REAL_API && (
        <div className="rounded-lg bg-[color-mix(in_srgb,var(--color-warning)_12%,var(--color-surface))] p-3 text-xs text-[var(--color-warning)] ring-1 ring-[color-mix(in_srgb,var(--color-warning)_38%,var(--color-border))]">
          {t.simulationNotice}
        </div>
      )}

      {query.isLoading ? (
        <div className="rounded-lg bg-[var(--color-surface)] p-8 text-center text-sm text-[var(--color-text-muted)] ring-1 ring-[var(--color-border)]">
          {t.loading}
        </div>
      ) : query.error ? (
        <div className="rounded-lg bg-[var(--color-surface)] p-8 text-center text-sm text-[var(--color-danger)] ring-1 ring-[var(--color-border)]">
          {t.loadError}
        </div>
      ) : (
        <AluraCard
          // Re-mount after a delete so a stale connection-test result disappears.
          key={current?.id ?? 'none'}
          current={current}
          onChanged={() => queryClient.invalidateQueries({ queryKey: QUERY_KEY })}
        />
      )}
    </div>
  )
}

function AluraCard({
  current,
  onChanged,
}: {
  current: ResearchToolCredentialRead | null
  onChanged: () => void
}) {
  const t = useT(researchToolsI18n)
  const configured = Boolean(current?.has_api_key)
  const gate = useEditGate(configured)
  const [apiKey, setApiKey] = useState('')
  const [confirmingRemove, setConfirmingRemove] = useState(false)
  const [testResult, setTestResult] = useState<ConnectionTest | null>(null)

  const save = useMutation({
    mutationFn: () =>
      request<ResearchToolCredentialRead>(`${BASE}/alura`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: apiKey.trim() }),
      }),
    onSuccess: () => {
      setApiKey('')
      setTestResult(null)
      gate.lock()
      onChanged()
      toast.success(t.savedToast, {
        description: t.savedToastDescription,
        audience: 'admin',
      })
    },
    onError: (error: unknown) => {
      toast.error(t.saveErrorToast, {
        description: error instanceof Error ? error.message : t.unknownError,
        audience: 'admin',
      })
    },
  })

  const test = useMutation({
    mutationFn: () => request<ConnectionTest>(`${BASE}/alura/test`, { method: 'POST' }),
    onSuccess: (result) => {
      setTestResult(result)
      if (result?.ok) onChanged()
    },
    onError: (error: unknown) => {
      setTestResult({
        ok: false,
        workspace_name: null,
        plan: null,
        credits_remaining: null,
        error_code: null,
        message: error instanceof Error ? error.message : t.unknownError,
      })
    },
  })

  const remove = useMutation({
    mutationFn: () => request<null>(`${BASE}/alura`, { method: 'DELETE' }),
    onSuccess: () => {
      setConfirmingRemove(false)
      onChanged()
      toast.success(t.removedToast, { audience: 'admin' })
    },
    onError: (error: unknown) => {
      toast.error(t.removeErrorToast, {
        description: error instanceof Error ? error.message : t.unknownError,
        audience: 'admin',
      })
    },
  })

  const canSave = USE_REAL_API && apiKey.trim().length > 0 && !save.isPending

  return (
    <section className="overflow-hidden rounded-lg bg-[var(--color-surface)] ring-1 ring-[var(--color-border)]" aria-label="Alura">
      <div className="flex items-center justify-between gap-4 border-b border-[var(--color-border)] px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[color-mix(in_srgb,var(--color-info)_12%,var(--color-surface))] text-[var(--color-info)]">
            <Telescope size={18} aria-hidden="true" />
          </div>
          <div>
            <div className="text-sm font-semibold text-[var(--color-text)]">Alura</div>
            <div className="text-[11px] text-[var(--color-text-muted)]">{t.providerSubtitle}</div>
          </div>
        </div>
        <ConfiguredBadge configured={configured} />
      </div>

      {!gate.editing ? (
        <div className="space-y-3 px-5 py-5">
          <LockedCredentialNotice hint={current?.api_key_hint} onEdit={gate.requestEdit} label={t.credentialsLabel} />
          {current && (
            <>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-[var(--color-text-muted)]">
                <span>{t.updated(formatDateTime(current.updated_at))}</span>
                <span>
                  {current.last_validated_at ? t.lastChecked(formatDateTime(current.last_validated_at)) : t.neverChecked}
                </span>
                {confirmingRemove ? (
                  <span className="inline-flex items-center gap-2">
                    <span>{t.confirmRemove}</span>
                    <button
                      type="button"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate()}
                      className="font-semibold text-[var(--color-danger)] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {t.yesRemove}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmingRemove(false)}
                      className="font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                    >
                      {t.cancel}
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmingRemove(true)}
                    className="font-medium text-[var(--color-danger)] hover:text-[var(--color-danger)]"
                  >
                    {t.remove}
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-3 border-t border-[var(--color-border)] pt-3 sm:flex-row sm:items-start">
                <button
                  type="button"
                  disabled={!USE_REAL_API || test.isPending}
                  onClick={() => test.mutate()}
                  className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-md border border-[var(--color-border-strong)] px-3 py-1.5 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-muted)] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <PlugZap size={13} aria-hidden="true" />
                  {test.isPending ? t.testing : t.testConnection}
                </button>
                {testResult && <TestResult result={testResult} />}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4 px-5 py-5">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-[var(--color-text)]">{t.apiKey}</span>
            <span className="mb-2 block text-[11px] text-[var(--color-text-muted)]">{t.apiKeyHint}</span>
            <input
              type="password"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              placeholder="••••••••"
              autoComplete="off"
              className={INPUT_CLASS}
            />
          </label>
          <p className="rounded-md bg-[var(--color-surface-muted)] px-3 py-2 text-xs text-[var(--color-text-muted)]">
            {t.savingNote}
          </p>
          <div className="flex items-center justify-end gap-2 border-t border-[var(--color-border)] pt-4">
            {configured && (
              <button
                type="button"
                onClick={gate.lock}
                className="rounded-md border border-[var(--color-border-strong)] px-4 py-2 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]"
              >
                {t.cancel}
              </button>
            )}
            <button
              type="button"
              disabled={!canSave}
              onClick={() => save.mutate()}
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <KeyRound size={13} aria-hidden="true" />
              {save.isPending ? t.saving : current ? t.rotateCredentials : t.saveCredentials}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

function TestResult({ result }: { result: ConnectionTest }) {
  const t = useT(researchToolsI18n)
  if (!result.ok) {
    const reason = (result.error_code && t.testErrors[result.error_code]) || result.message || t.unknownError
    return (
      <div role="status" className="flex items-start gap-2 text-xs text-[var(--color-danger)]">
        <XCircle size={14} className="mt-px shrink-0" aria-hidden="true" />
        <span>
          <span className="font-semibold">{t.testFailed}</span> — {reason}
        </span>
      </div>
    )
  }
  return (
    <div role="status" className="space-y-1 text-xs text-[var(--color-text)]">
      <div className="flex items-center gap-2 font-semibold text-[var(--color-success-foreground)]">
        <CheckCircle2 size={14} aria-hidden="true" />
        {t.testOk}
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[var(--color-text-muted)]">
        {result.workspace_name && (
          <>
            <dt>{t.workspace}</dt>
            <dd className="text-[var(--color-text)]">{result.workspace_name}</dd>
          </>
        )}
        {result.plan && (
          <>
            <dt>{t.plan}</dt>
            <dd className="text-[var(--color-text)]">{result.plan}</dd>
          </>
        )}
        <dt>{t.creditsRemaining}</dt>
        <dd className="font-semibold tabular-nums text-[var(--color-text)]">
          {result.credits_remaining == null ? t.creditsUnknown : new Intl.NumberFormat(intlLocale()).format(result.credits_remaining)}
        </dd>
      </dl>
    </div>
  )
}
